/**
 * Cloud Operations — SLA matrix.
 *
 * Provider tabs · region × tier table · color-coded cells.
 * Per design doc Mockup "SLA matrix" + backend SlaMatrixService.
 */

import React, { useEffect, useState } from 'react';
import { getSlaMatrix } from '../../services/cloudOperationsApi';
import { T, Banner } from './_ui';

const TIER_LABELS = {
  single_az:                  'Single-AZ',
  multi_az:                   'Multi-AZ',
  multi_region_active_standby:'Multi-Region · A/S',
  multi_region_active_active: 'Multi-Region · A/A',
};

export default function SlaMatrixPage() {
  const [matrix, setMatrix] = useState(null);
  const [provider, setProvider] = useState('gcp');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSlaMatrix().then(setMatrix).then(() => setLoading(false)).catch((e) => {
      setError(e?.message || String(e));
      setLoading(false);
    });
  }, []);

  if (loading) return <Banner color={T.info} bg={T.infoBg}>Loading SLA matrix…</Banner>;
  if (error)   return <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>;
  if (!matrix) return null;

  // Belt-and-suspenders: every Array consumer below defaults to [] so even
  // partial backend data can't crash the page. The API-client unwrap should
  // ensure these fields are populated; the guards are insurance.
  const tiers       = Array.isArray(matrix.tiers)        ? matrix.tiers        : [];
  const providers   = Array.isArray(matrix.providers)    ? matrix.providers    : [];
  const tierDetails = Array.isArray(matrix.tier_details) ? matrix.tier_details : [];
  const regions     = Array.isArray(matrix.by_provider?.[provider])
                        ? matrix.by_provider[provider]
                        : [];

  return (
    <div>
      <Header lastReviewed={matrix.last_reviewed} />
      <ProviderTabs current={provider} onChange={setProvider} providers={providers} />
      <TierLegend tiers={tierDetails} />
      <MatrixTable tiers={tiers} regions={regions} />
      <Footnote />
    </div>
  );
}

function Header({ lastReviewed }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: T.text2, fontSize: 13 }}>
        Availability + RTO + RPO commitments by provider, region, and tier. The wizard restricts
        customer choices to combinations the platform can <strong>actually</strong> operationalize —
        cells are populated as failover and bring-up workflows ship.
      </div>
      <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
        Last reviewed: {lastReviewed || '—'}
      </div>
    </div>
  );
}

function ProviderTabs({ current, onChange, providers }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${T.stroke}`, marginBottom: 16 }}>
      {providers.map((p) => (
        <button key={p} onClick={() => onChange(p)} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
          color: current === p ? T.ink : T.text2,
          background: 'transparent', border: 'none',
          borderBottom: `2px solid ${current === p ? T.action : 'transparent'}`,
          marginBottom: -1, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '.04em',
        }}>{p}</button>
      ))}
    </div>
  );
}

function TierLegend({ tiers }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(${tiers.length}, 1fr)`,
      gap: 8, marginBottom: 16,
    }}>
      {tiers.map((tier) => (
        <div key={tier.id} style={{
          background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 4,
          padding: 12, fontSize: 12,
        }}>
          <div style={{ fontWeight: 600, color: T.ink, fontSize: 13 }}>{tier.name}</div>
          <div style={{ color: T.text2, marginTop: 4 }}>
            <div>availability {tier.availability}</div>
            <div>RTO {tier.rto}  ·  RPO {tier.rpo}</div>
            <div style={{ marginTop: 6, fontStyle: 'italic', color: T.text3 }}>{tier.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MatrixTable({ tiers, regions }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 6, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgTint }}>
            <th style={hdr}>Region</th>
            {tiers.map((t) => <th key={t} style={hdr}>{TIER_LABELS[t] || t}</th>)}
            <th style={hdr}>Eligible workflows</th>
          </tr>
        </thead>
        <tbody>
          {regions.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
              <td style={cell}>
                <div style={{ fontWeight: 600, color: T.ink }}>{r.id}</div>
                <div style={{ color: T.text3, fontSize: 11 }}>{r.location}</div>
                {r.paired_region && <div style={{ color: T.text2, fontSize: 11 }}>paired ↔ {r.paired_region}</div>}
              </td>
              {tiers.map((t) => {
                const c = (r && r.tiers && r.tiers[t]) || {};
                return <Cell key={t} data={c} />;
              })}
              <td style={cell}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(r.eligible_workflows || []).map((w) => (
                    <code key={w} style={{ fontSize: 11, color: T.text2 }}>{w}</code>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// NB: prop renamed `data` to avoid shadowing the `cell` style constant below.
function Cell({ data }) {
  const cls = !data.supported
    ? { background: T.bgTint, color: T.text3, label: '—', sub: 'not supported' }
    : data.workflow_active
      ? { background: 'rgba(46,125,79,.08)', color: T.text, label: '✓', sub: data.note || 'workflow active' }
      : { background: 'rgba(197,139,0,.08)', color: T.text, label: '~', sub: data.note || 'workflow draft' };
  return (
    <td style={{ ...cell, background: cls.background }}>
      <div style={{ fontWeight: 700, color: cls.color }}>{cls.label}</div>
      <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{cls.sub}</div>
    </td>
  );
}

function Footnote() {
  return (
    <div style={{ marginTop: 16, fontSize: 11, color: T.text3 }}>
      <strong>Cell legend:</strong> ✓ = supported infrastructurally AND workflow is Active (offer in wizard);{' '}
      <em>~</em> = infrastructurally possible but workflow in draft (do not promise the SLA);{' '}
      — = nothing to deploy yet.
    </div>
  );
}

const hdr = {
  textAlign: 'left', padding: '10px 14px', fontSize: 11,
  textTransform: 'uppercase', letterSpacing: '.06em',
  color: T.text2, fontWeight: 600, borderBottom: `1px solid ${T.stroke}`,
};
const cell = { padding: '12px 14px', verticalAlign: 'top' };
